
import app from "@/app";
import {
	OZUser,
	SXCompany,
	SXSurvey,
	SXQuestion,
	SXChoice
} from "@/gobl.bundle";
import { tI18nData, tI18nPluralize, Utils, tFileQuality } from "oweb";
import { Component, Prop, Vue } from "vue-property-decorator";

@Component
export default class BaseComponent extends Vue {
	get $app() {
		return app;
	}

	get $store() {
		return this.$app.store;
	}

	get $services() {
		return this.$app.services;
	}

	i18n(
		key: string,
		data: tI18nData = {},
		pluralize?: tI18nPluralize,
		lang?: string
	) {
		return app.i18n.toHuman(key, data, pluralize, lang);
	}

	ow_file_url(file: string, quality: tFileQuality = 0, def?: string) {
		let parts = file.split("_"),
			url = app.url.get("OZ_SERVER_GET_FILE_URI"),
			o = "0",
			file_id = o,
			file_key = o;

		if (parts.length === 2) {
			file_id = parts[0];
			file_key = parts[1];
		}

		if (def && (file_id === o || file_key === o)) {
			return def;
		}

		let data: any = {
			"{oz_file_id}": file_id,
			"{oz_file_key}": file_key,
			"{oz_file_quality}": quality
		};

		Object.keys(data).forEach(function(key) {
			url = url.replace(key, data[key]);
		});

		return url;
	}
	ow_local_time(time: string | number) {
		let offset = new Date().getTimezoneOffset() * 60;

		if (typeof time === "string") {
			time = parseInt(time);
		}

		return (time + offset) * 1000;
	}
	ow_route_link(path: string): string {
		return app.router.pathToURL(path).href;
	}
	ow_loop_key(value: any) {
		const key = "__ow_key_id",
			type = typeof value;

		if (type === "object" || type === "function") {
			return key in value ? value[key] : (value[key] = Utils.id());
		}

		return JSON.stringify(value);
	}
	ow_browse_to(route: string) {
		app.router.browseTo(route);
	}

	// ****************************************
	// *           CUSTOMS METHODS            *
	// ****************************************
	ux_company_image(c: SXCompany) {
		return c && this.ow_file_url(c.image, 0, COMPANY_PIC);
	}
	ux_survey_image(c: SXSurvey) {
		let picid = c.image;
		if (picid === "0_0") {
			let company = app.services.companies.identify(c.company_id);
			if (company) {
				picid = company.image;
			}
		}
		return c && this.ow_file_url(picid, 0, SURVEY_PIC);
	}
	ux_span_label(field: any) {
		if (field.spanLabel) {
			return app.i18n.toHuman(field.spanLabel);
		}

		return "";
	}
	ux_user_pic(user: OZUser, quality: 0 | 1 | 2 | 3 = 0) {
		return this.ow_file_url(user.getPicid(), quality, USER_PIC);
	}
	ux_uuid() {
		return ("" + 1e7 + -1e3 + -4e3 + -8e3 + -1e11).replace(
			/[018]/g,
			(c: any) => {
				return (
					c ^
					(crypto.getRandomValues(new Uint8Array(1))[0] &
						(15 >> (c / 4)))
				).toString(16);
			}
		);
	}
	ux_question_data(q: SXQuestion) {
		let data = {
			dirty: false,
			required: false,
			subLabel: "",
			typeOptions: {
				min: 0,
				max: 0,
				othersChoice: false,
				othersChoiceUuid: this.ux_uuid(),
				choices: []
			}
		};

		try {
			data = Utils.assign(data, JSON.parse(q.data) || {});
		} catch (e) {}

		return data;
	}
	ux_answer_valid(q: SXQuestion, answer: any) {
		let options = this.ux_question_data(q);

		if (!options.required && !answer) {
			return true;
		}

		return !(
			!answer ||
			!answer.isValid ||
			(options.required && answer.isEmpty)
		);
	}

	ux_custom_data(data: string, def: any = {}) {
		let parsed: any;
		try {
			parsed = JSON.parse(data);
		} catch (e) {}

		return parsed || def;
	}
	ux_clone_choice(choice: SXChoice) {
		let clone = new SXChoice(),
			data = choice.toObjectSome([SXChoice.COL_LABEL, SXChoice.COL_DATA]);
		return clone.doHydrate({
			...data,
			[SXChoice.COL_UUID]: this.ux_uuid()
		});
	}
	ux_clone_question(question: SXQuestion) {
		let x = this,
			clone = new SXQuestion(),
			questionData = this.ux_custom_data(question.data),
			typeOptions = questionData.typeOptions,
			customData = {
				dirty: questionData.dirty,
				required: questionData.required,
				subLabel: questionData.subLabel,
				typeOptions: {
					min: typeOptions.min,
					max: typeOptions.max,
					othersChoice: typeOptions.othersChoice,
					othersChoiceUuid: this.ux_uuid(),
					choices: typeOptions.choices.map(function(
						choice: SXChoice
					) {
						return x.ux_clone_choice(choice);
					})
				}
			},
			data = question.toObjectSome([
				SXQuestion.COL_SURVEY_ID,
				SXQuestion.COL_LABEL,
				SXQuestion.COL_TYPE
			]);

		return clone.doHydrate({
			...data,
			[SXQuestion.COL_UUID]: this.ux_uuid(),
			[SXQuestion.COL_DATA]: JSON.stringify(customData)
		});
	}
	ux_go_back() {
		app.router.goBack();
	}
	ux_can_go_back() {
		return window.history.length > 1;
	}

	ux_download(
		content: string,
		filename: string,
		type: string = "application/octet-stream"
	) {
		let a = document.createElement("a");

		if (navigator.msSaveBlob) {
			// IE10
			navigator.msSaveBlob(new Blob([content], { type }), filename);
		} else if (URL && "download" in a) {
			//HTML5
			a.href = URL.createObjectURL(new Blob([content], { type }));
			a.setAttribute("download", filename);
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		} else {
			type = "application/octet-stream"; // only this mime type is supported
			let encoded = encodeURIComponent(content);
			location.href = `data:${type},${encoded}`;
		}
	}
	ux_array_to_csv_text(rows: Array<Array<any>>) {
		let processRow = function(row: Array<any>) {
			let finalVal = "";
			for (let j = 0; j < row.length; j++) {
				let innerValue = row[j] === null ? "" : row[j].toString();
				if (row[j] instanceof Date) {
					innerValue = row[j].toLocaleString();
				}
				let result = innerValue.replace(/"/g, '""');
				if (result.search(/("|,|\n)/g) >= 0) result = `"${result}"`;
				if (j > 0) finalVal += ",";
				finalVal += result;
			}
			return finalVal + "\n";
		};

		let content = "";
		for (let i = 0; i < rows.length; i++) {
			content += processRow(rows[i]);
		}
		return content;
	}
	ux_to_utf8_array(str: string) {
		// see https://gist.github.com/joni/3760795/8f0c1a608b7f0c8b3978db68105c5b1d741d0446

		let utf8 = [];
		for (let i = 0; i < str.length; i++) {
			let charcode = str.charCodeAt(i);
			if (charcode < 0x80) utf8.push(charcode);
			else if (charcode < 0x800) {
				utf8.push(0xc0 | (charcode >> 6), 0x80 | (charcode & 0x3f));
			} else if (charcode < 0xd800 || charcode >= 0xe000) {
				utf8.push(
					0xe0 | (charcode >> 12),
					0x80 | ((charcode >> 6) & 0x3f),
					0x80 | (charcode & 0x3f)
				);
			}
			// surrogate pair
			else {
				i++;
				charcode =
					((charcode & 0x3ff) << 10) | (str.charCodeAt(i) & 0x3ff);
				utf8.push(
					0xf0 | (charcode >> 18),
					0x80 | ((charcode >> 12) & 0x3f),
					0x80 | ((charcode >> 6) & 0x3f),
					0x80 | (charcode & 0x3f)
				);
			}
		}

		return utf8;
	}
}

const USER_PIC = require("../assets/images/user_pic.png"),
	COMPANY_PIC = require("../assets/images/company_pic.png"),
	SURVEY_PIC = COMPANY_PIC;
